// Canvas particle engine for the festive themes (2026-10-04, v2). One full-screen canvas, never takes
// clicks. Physics are per-frame at 60 fps and scaled by real elapsed time, so a slow device just runs
// slower rather than skipping. Drawn with normal (source-over) blending because the app behind it is
// white — additive "glow" blending washes out to nothing on a light page.

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
export const hex = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const mix = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t].map(Math.round);

// Saturated colours that still read on a white page.
export const FIREWORK_COLORS = ['#e11d48', '#f97316', '#d97706', '#16a34a', '#2563eb', '#c026d3', '#0891b2', '#dc2626', '#7c3aed', '#ca8a04'];

const MAX_PARTICLES = 1800;

export class FxEngine {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.parts = [];      // sparks, flames, smoke, embers, dust, gifts, arrows, flashes
        this.rockets = [];
        this.ambient = [];    // snow / petals / bokeh — recycled, never removed
        this.fires = new Map();
        this.sprites = new Map();
        this.resize();
    }

    resize() {
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        this.dpr = dpr;
        this.W = window.innerWidth;
        this.H = window.innerHeight;
        this.canvas.width = Math.round(this.W * dpr);
        this.canvas.height = Math.round(this.H * dpr);
        this.canvas.style.width = `${this.W}px`;
        this.canvas.style.height = `${this.H}px`;
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.small = this.W < 640;
    }

    // Soft round sprite: solid core fading out — the "glow" of a spark without additive blending.
    sprite(c) {
        const key = c.join(',');
        if (this.sprites.has(key)) return this.sprites.get(key);
        const s = document.createElement('canvas');
        s.width = s.height = 64;
        const g = s.getContext('2d');
        const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
        grd.addColorStop(0, rgba(mix(c, [255, 255, 255], 0.35), 1));
        grd.addColorStop(0.22, rgba(c, 1));
        grd.addColorStop(0.5, rgba(c, 0.28));
        grd.addColorStop(1, rgba(c, 0));
        g.fillStyle = grd;
        g.fillRect(0, 0, 64, 64);
        this.sprites.set(key, s);
        return s;
    }

    count() { return this.parts.length; }

    // ── Fireworks ───────────────────────────────────────────────────────────────────────────
    launch(x, apexY, opts = {}) {
        const g = 0.055;
        const h = Math.max(60, this.H - apexY);
        this.rockets.push({
            x, y: this.H + 10, vx: rand(-0.35, 0.35), vy: -Math.sqrt(2 * g * h), g,
            color: opts.color || pick(FIREWORK_COLORS), type: opts.type, scale: opts.scale || 1,
        });
    }

    burst(x, y, opts = {}) {
        const c = hex(opts.color || pick(FIREWORK_COLORS));
        const type = opts.type || pick(['peony', 'peony', 'ring', 'willow', 'crackle', 'double']);
        const s = (opts.scale || 1) * (this.small ? 0.72 : 1);
        if (this.parts.length > MAX_PARTICLES) return;
        this.parts.push({ k: 'flash', x, y, life: 9, max: 9, c, size: 70 * s });
        const add = (n, fn) => { for (let i = 0; i < n; i++) this.parts.push(fn(i, n)); };
        const spark = (vx, vy, extra) => ({ k: 'spark', x, y, vx, vy, drag: 0.972, g: 0.032, life: rand(52, 80), c,
            size: rand(1.5, 2.3) * Math.max(0.8, s), trail: [], tl: 6, ...extra });
        if (type === 'ring') {
            const sp = 3.3 * s;
            add(64, (i, n) => { const a = (i / n) * Math.PI * 2; return spark(Math.cos(a) * sp, Math.sin(a) * sp * 0.9, { drag: 0.976 }); });
        } else if (type === 'willow') {
            const gold = [202, 138, 4];
            add(74, () => { const a = rand(0, Math.PI * 2), sp = rand(0.9, 3.1) * s;
                return spark(Math.cos(a) * sp, Math.sin(a) * sp, { c: gold, drag: 0.983, g: 0.022, life: rand(100, 140), tl: 12, size: 1.4, glitter: true }); });
        } else if (type === 'crackle') {
            add(70, () => { const a = rand(0, Math.PI * 2), sp = rand(1.2, 4) * s;
                return spark(Math.cos(a) * sp, Math.sin(a) * sp, { crackle: true }); });
        } else if (type === 'double') {
            const c2 = hex(pick(FIREWORK_COLORS));
            add(56, () => { const a = rand(0, Math.PI * 2), sp = rand(2.6, 4.2) * s; return spark(Math.cos(a) * sp, Math.sin(a) * sp); });
            add(36, () => { const a = rand(0, Math.PI * 2), sp = rand(0.8, 1.8) * s; return spark(Math.cos(a) * sp, Math.sin(a) * sp, { c: c2 }); });
        } else {
            add(84, () => { const a = rand(0, Math.PI * 2), sp = rand(1.1, 4.3) * s;
                return spark(Math.cos(a) * sp, Math.sin(a) * sp, { glitter: Math.random() < 0.2 }); });
        }
    }

    // Small spray of sparks — an arrow strike.
    impact(x, y, color = '#f97316') {
        const c = hex(color);
        this.parts.push({ k: 'flash', x, y, life: 7, max: 7, c, size: 46 });
        for (let i = 0; i < 26; i++) {
            const a = rand(Math.PI * 0.55, Math.PI * 1.45), sp = rand(1.5, 4.5);
            this.parts.push({ k: 'spark', x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1, drag: 0.95, g: 0.09,
                life: rand(20, 36), c: Math.random() < 0.5 ? c : [234, 179, 8], size: rand(1.2, 2), trail: [], tl: 4 });
        }
    }

    // Two arrows meeting mid-air.
    clash(x, y) {
        this.parts.push({ k: 'flash', x, y, life: 10, max: 10, c: [250, 204, 21], size: 90 });
        for (let i = 0; i < 46; i++) {
            const a = rand(0, Math.PI * 2), sp = rand(1.2, 4.4);
            this.parts.push({ k: 'spark', x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, drag: 0.95, g: 0.05,
                life: rand(22, 40), c: i % 3 === 0 ? [168, 85, 247] : i % 3 === 1 ? [234, 88, 12] : [202, 138, 4], size: rand(1.3, 2.2), trail: [], tl: 4 });
        }
    }

    // ── Fire (Ravan burning) ────────────────────────────────────────────────────────────────
    setFire(id, f) { if (f) this.fires.set(id, f); else this.fires.delete(id); }

    // ── Arrow: a flaming shaft flying from a to b; calls onHit on arrival ─────────────────────
    // opts.flame: flame colour at the tip (Ravan's arrows burn red-violet).
    arrow(x0, y0, x1, y1, dur, onHit, opts = {}) {
        this.parts.push({ k: 'arrow', x: x0, y: y0, x0, y0, x1, y1, t: 0, dur, onHit, life: 1, max: 1, flame: hex(opts.flame || '#ea580c') });
    }

    // ── Ambient fields ──────────────────────────────────────────────────────────────────────
    setAmbient(kind, n) {
        this.ambient = this.ambient.filter(a => a.k !== kind);
        for (let i = 0; i < n; i++) this.ambient.push(this.makeAmbient(kind, true));
    }

    makeAmbient(kind, scatter) {
        const W = this.W, H = this.H;
        if (kind === 'snow') {
            const z = rand(0.3, 1);   // depth: far flakes are small, slow, faint
            return { k: 'snow', x: rand(0, W), y: scatter ? rand(-H, H) : rand(-60, -10), z, r: 1 + z * 3.2,
                vy: 0.35 + z * 1.1, sway: rand(0, Math.PI * 2), sw: rand(0.004, 0.012) };
        }
        if (kind === 'petal') {
            return { k: 'petal', x: rand(0, W), y: scatter ? rand(-H, H) : rand(-60, -10), vy: rand(0.6, 1.3),
                vx: rand(-0.3, 0.3), rot: rand(0, Math.PI * 2), vr: rand(-0.03, 0.03), flip: rand(0, Math.PI * 2),
                vf: rand(0.03, 0.07), size: rand(5, 9), c: pick([[234, 88, 12], [245, 158, 11], [249, 115, 22], [220, 38, 38], [250, 204, 21]]) };
        }
        // bokeh: soft discs of light drifting up
        return { k: 'bokeh', x: rand(0, W), y: scatter ? rand(0, H) : H + 30, vy: -rand(0.15, 0.45), vx: rand(-0.1, 0.1),
            r: rand(6, 20), a: rand(0.07, 0.17), c: pick([[219, 39, 119], [245, 158, 11], [147, 51, 234], [234, 88, 12]]),
            tw: rand(0, Math.PI * 2) };
    }

    dust(x, y) {   // magic sparkle behind the sleigh
        this.parts.push({ k: 'dust', x: x + rand(-6, 6), y: y + rand(-6, 6), vx: rand(-0.6, -0.1), vy: rand(-0.1, 0.5),
            life: rand(30, 60), max: 60, size: rand(1.2, 3), c: pick([[202, 138, 4], [234, 179, 8], [148, 163, 184], [96, 165, 250]]) });
    }

    gift(x, y, vx) {
        const palettes = [[[220, 38, 38], [250, 204, 21]], [[37, 99, 235], [248, 250, 252]], [[22, 163, 74], [220, 38, 38]],
            [[147, 51, 234], [250, 204, 21]], [[234, 88, 12], [22, 163, 74]], [[219, 39, 119], [248, 250, 252]]];
        const [c, r] = pick(palettes);
        this.parts.push({ k: 'gift', x, y, vx: vx + rand(-0.4, 0.4), vy: rand(-1.4, -0.4), rot: rand(0, 1), vr: rand(-0.06, 0.06),
            size: (this.small ? 13 : 18) * rand(0.85, 1.15), c, r, life: 1, max: 1 });
    }

    // ── Simulation ──────────────────────────────────────────────────────────────────────────
    step(k) {
        const H = this.H, W = this.W;
        for (let i = this.rockets.length - 1; i >= 0; i--) {
            const r = this.rockets[i];
            r.vy += r.g * k; r.x += r.vx * k; r.y += r.vy * k;
            if (Math.random() < 0.9) this.parts.push({ k: 'spark', x: r.x, y: r.y + 4, vx: rand(-0.3, 0.3), vy: rand(0.4, 1.4),
                drag: 0.94, g: 0.02, life: rand(14, 26), c: [217, 119, 6], size: rand(1, 1.6), trail: [], tl: 2 });
            if (r.vy >= -0.4) { this.burst(r.x, r.y, { color: r.color, type: r.type, scale: r.scale }); this.rockets.splice(i, 1); }
        }
        for (const [, f] of this.fires) {
            const n = Math.round(f.power * (this.small ? 3 : 5) * k);
            for (let i = 0; i < n; i++) {
                const x = f.x + rand(-f.w / 2, f.w / 2) * (1 - Math.random() * 0.3);
                this.parts.push({ k: 'flame', x, y: f.y + rand(-f.h, 0) * Math.random(), vx: rand(-0.35, 0.35), vy: -rand(1.1, 2.6) * (0.7 + f.power * 0.3),
                    life: rand(26, 48), max: 48, size: rand(7, 15) * (0.7 + f.power * 0.35) * (this.small ? 0.7 : 1) });
                if (Math.random() < 0.18) this.parts.push({ k: 'smoke', x, y: f.y - f.h * rand(0.6, 1), vx: rand(-0.3, 0.3), vy: -rand(0.5, 1.1),
                    life: rand(70, 120), max: 120, size: rand(10, 18) });
                if (Math.random() < 0.12) this.parts.push({ k: 'ember', x, y: f.y - rand(0, f.h), vx: rand(-0.6, 0.6), vy: -rand(1.2, 2.8),
                    life: rand(50, 90), max: 90, size: rand(1, 2) });
            }
        }
        const out = [];
        for (const p of this.parts) {
            switch (p.k) {
                case 'spark': {
                    if (p.tl) { p.trail.push(p.x, p.y); if (p.trail.length > p.tl * 2) p.trail.splice(0, 2); }
                    p.vx *= Math.pow(p.drag, k); p.vy = p.vy * Math.pow(p.drag, k) + p.g * k;
                    p.x += p.vx * k; p.y += p.vy * k; p.life -= k;
                    if (p.life <= 0 && p.crackle) {
                        for (let j = 0; j < 3; j++) out.push({ k: 'spark', x: p.x, y: p.y, vx: rand(-1.2, 1.2), vy: rand(-1.2, 1.2), drag: 0.9, g: 0.02,
                            life: rand(8, 16), c: [202, 138, 4], size: 1.1, trail: [], tl: 0 });
                    }
                    break;
                }
                case 'flame': p.x += p.vx * k; p.y += p.vy * k; p.vx += rand(-0.08, 0.08); p.life -= k; break;
                case 'smoke': p.x += p.vx * k; p.y += p.vy * k; p.size += 0.22 * k; p.life -= k; break;
                case 'ember': p.x += p.vx * k + Math.sin(p.life * 0.2) * 0.3; p.y += p.vy * k; p.vy *= 0.995; p.life -= k; break;
                case 'dust': p.x += p.vx * k; p.y += p.vy * k; p.life -= k; break;
                case 'flash': p.life -= k; break;
                case 'gift': p.vy += 0.075 * k; p.x += p.vx * k; p.y += p.vy * k; p.vx *= 0.995; p.rot += p.vr * k; if (p.y > H + 40) p.life = 0; break;
                case 'arrow': {
                    p.t += (16.67 * k) / p.dur;
                    const t = Math.min(1, p.t);
                    const x = p.x0 + (p.x1 - p.x0) * t, y = p.y0 + (p.y1 - p.y0) * t - Math.sin(t * Math.PI) * 18;
                    p.x = x; p.y = y;   // kept current: the off-screen cull below reads p.x
                    if (Math.random() < 0.9) out.push({ k: 'ember', x: x - 6 * Math.sign(p.x1 - p.x0 || 1), y, vx: rand(-1.4, -0.4), vy: rand(-0.6, 0.4), life: rand(14, 26), max: 26, size: rand(1, 1.8) });
                    if (t >= 1) { p.life = 0; if (p.onHit) p.onHit(); }
                    break;
                }
                default: break;
            }
            if (p.life > 0 && p.x > -200 && p.x < W + 200) out.push(p);
        }
        this.parts = out;
        for (let i = 0; i < this.ambient.length; i++) {
            const a = this.ambient[i];
            if (a.k === 'snow') { a.sway += a.sw * k; a.x += Math.sin(a.sway) * 0.45 * a.z * k + 0.12 * k; a.y += a.vy * k; }
            else if (a.k === 'petal') { a.x += (a.vx + Math.sin(a.flip * 0.5) * 0.4) * k; a.y += a.vy * k; a.rot += a.vr * k; a.flip += a.vf * k; }
            else { a.x += a.vx * k; a.y += a.vy * k; a.tw += 0.02 * k; }
            if (a.y > H + 30 || a.y < -60 || a.x < -40 || a.x > W + 40) this.ambient[i] = this.makeAmbient(a.k, false);
        }
    }

    draw() {
        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.W, this.H);
        // ambient
        for (const a of this.ambient) {
            if (a.k === 'snow') {
                const g = ctx.createRadialGradient(a.x, a.y, 0, a.x, a.y, a.r);
                g.addColorStop(0, `rgba(203,213,225,${0.95 * a.z})`);
                g.addColorStop(0.6, `rgba(148,163,184,${0.55 * a.z})`);
                g.addColorStop(1, 'rgba(148,163,184,0)');
                ctx.fillStyle = g; ctx.beginPath(); ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2); ctx.fill();
            } else if (a.k === 'petal') {
                ctx.save(); ctx.translate(a.x, a.y); ctx.rotate(a.rot); ctx.scale(1, Math.max(0.15, Math.abs(Math.cos(a.flip))));
                const g = ctx.createLinearGradient(-a.size, 0, a.size, 0);
                g.addColorStop(0, rgba(mix(a.c, [120, 30, 0], 0.35), 0.95)); g.addColorStop(0.5, rgba(a.c, 0.95)); g.addColorStop(1, rgba(mix(a.c, [255, 230, 120], 0.3), 0.95));
                ctx.fillStyle = g; ctx.beginPath(); ctx.ellipse(0, 0, a.size, a.size * 0.55, 0, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = rgba(mix(a.c, [90, 20, 0], 0.5), 0.5); ctx.lineWidth = 0.6; ctx.beginPath(); ctx.moveTo(-a.size * 0.8, 0); ctx.lineTo(a.size * 0.8, 0); ctx.stroke();
                ctx.restore();
            } else {
                const al = a.a * (0.7 + 0.3 * Math.sin(a.tw));
                const g = ctx.createRadialGradient(a.x, a.y, 0, a.x, a.y, a.r);
                g.addColorStop(0, rgba(a.c, al)); g.addColorStop(0.7, rgba(a.c, al * 0.6)); g.addColorStop(1, rgba(a.c, 0));
                ctx.fillStyle = g; ctx.beginPath(); ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2); ctx.fill();
            }
        }
        // smoke behind flames
        for (const p of this.parts) {
            if (p.k !== 'smoke') continue;
            const t = p.life / p.max;
            const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
            g.addColorStop(0, `rgba(87,83,78,${0.16 * t})`); g.addColorStop(1, 'rgba(87,83,78,0)');
            ctx.fillStyle = g; ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
        }
        for (const p of this.parts) {
            switch (p.k) {
                case 'flame': {
                    const t = p.life / p.max;   // 1 = just born (hot)
                    const c = t > 0.7 ? mix([250, 204, 21], [254, 240, 138], (t - 0.7) / 0.3) : t > 0.4 ? mix([234, 88, 12], [250, 204, 21], (t - 0.4) / 0.3) : mix([127, 29, 29], [234, 88, 12], t / 0.4);
                    const r = p.size * (0.45 + t * 0.7);
                    const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
                    g.addColorStop(0, rgba(c, 0.85 * Math.min(1, t * 1.6))); g.addColorStop(1, rgba(c, 0));
                    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill();
                    break;
                }
                case 'spark': {
                    const t = Math.max(0, p.life / 70);
                    let al = Math.min(1, t * 1.4);
                    if (p.glitter && Math.random() < 0.35) al *= 0.2;
                    if (p.trail.length >= 4) {
                        ctx.strokeStyle = rgba(p.c, al * 0.55); ctx.lineWidth = p.size * 0.8; ctx.lineCap = 'round';
                        ctx.beginPath(); ctx.moveTo(p.trail[0], p.trail[1]);
                        for (let j = 2; j < p.trail.length; j += 2) ctx.lineTo(p.trail[j], p.trail[j + 1]);
                        ctx.lineTo(p.x, p.y); ctx.stroke();
                    }
                    ctx.globalAlpha = al;
                    const sz = p.size * 5;
                    ctx.drawImage(this.sprite(p.c), p.x - sz / 2, p.y - sz / 2, sz, sz);
                    ctx.globalAlpha = 1;
                    break;
                }
                case 'flash': {
                    const t = p.life / p.max;
                    ctx.globalAlpha = t * 0.45;
                    ctx.drawImage(this.sprite(p.c), p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
                    ctx.globalAlpha = 1;
                    break;
                }
                case 'ember': {
                    const t = p.life / p.max;
                    ctx.fillStyle = `rgba(234,${Math.round(88 + 100 * t)},12,${Math.min(1, t * 1.3)})`;
                    ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
                    break;
                }
                case 'dust': {
                    const t = p.life / p.max, s = p.size * (0.6 + 0.4 * Math.sin(p.life * 0.5));
                    ctx.fillStyle = rgba(p.c, Math.min(1, t * 1.5));
                    ctx.beginPath(); ctx.moveTo(p.x, p.y - s * 2); ctx.lineTo(p.x + s * 0.5, p.y); ctx.lineTo(p.x, p.y + s * 2); ctx.lineTo(p.x - s * 0.5, p.y); ctx.closePath(); ctx.fill();
                    ctx.beginPath(); ctx.moveTo(p.x - s * 2, p.y); ctx.lineTo(p.x, p.y + s * 0.5); ctx.lineTo(p.x + s * 2, p.y); ctx.lineTo(p.x, p.y - s * 0.5); ctx.closePath(); ctx.fill();
                    break;
                }
                case 'gift': {
                    const s = p.size;
                    ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
                    const g = ctx.createLinearGradient(-s / 2, -s / 2, s / 2, s / 2);
                    g.addColorStop(0, rgba(mix(p.c, [255, 255, 255], 0.25), 1)); g.addColorStop(1, rgba(mix(p.c, [0, 0, 0], 0.3), 1));
                    ctx.fillStyle = g; ctx.fillRect(-s / 2, -s / 2 + s * 0.12, s, s * 0.88);
                    ctx.fillStyle = rgba(mix(p.c, [0, 0, 0], 0.15), 1); ctx.fillRect(-s * 0.56, -s / 2, s * 1.12, s * 0.22);
                    ctx.fillStyle = rgba(p.r, 1); ctx.fillRect(-s * 0.09, -s / 2, s * 0.18, s);
                    ctx.beginPath(); ctx.ellipse(-s * 0.2, -s * 0.6, s * 0.2, s * 0.12, -0.5, 0, Math.PI * 2); ctx.ellipse(s * 0.2, -s * 0.6, s * 0.2, s * 0.12, 0.5, 0, Math.PI * 2); ctx.fill();
                    ctx.restore();
                    break;
                }
                case 'arrow': {
                    const t = Math.min(1, p.t);
                    const x = p.x0 + (p.x1 - p.x0) * t, y = p.y0 + (p.y1 - p.y0) * t - Math.sin(t * Math.PI) * 18;
                    const dy = (p.y1 - p.y0) - Math.cos(t * Math.PI) * Math.PI * 18, dx = p.x1 - p.x0;
                    const ang = Math.atan2(dy, dx), L = this.small ? 34 : 52;
                    ctx.save(); ctx.translate(x, y); ctx.rotate(ang);
                    ctx.strokeStyle = '#78350f'; ctx.lineWidth = 2.2; ctx.beginPath(); ctx.moveTo(-L, 0); ctx.lineTo(0, 0); ctx.stroke();
                    ctx.strokeStyle = '#b91c1c'; ctx.lineWidth = 1.6; ctx.beginPath(); ctx.moveTo(-L, 0); ctx.lineTo(-L - 7, -5); ctx.moveTo(-L, 0); ctx.lineTo(-L - 7, 5); ctx.stroke();
                    ctx.fillStyle = '#44403c'; ctx.beginPath(); ctx.moveTo(8, 0); ctx.lineTo(-1, -4); ctx.lineTo(-1, 4); ctx.closePath(); ctx.fill();
                    ctx.globalAlpha = 0.9; ctx.drawImage(this.sprite(p.flame), -10, -10, 26, 20); ctx.globalAlpha = 1;
                    ctx.restore();
                    break;
                }
                default: break;
            }
        }
    }
}
